"use client";

import { motion } from "framer-motion";
import { Check, PlaneLanding, PlaneTakeoff, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 18
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
const arrivalBullets = ["Privat ohne weitere Fahrgäste", "Preis vorab vereinbart", "Kostenlose Flugüberwachung und bis zu 60 Minuten kostenlose Wartezeit ab dem vereinbarten Treffzeitpunkt", "Barzahlung bei Ankunft im Hotel"];
const returnBullets = ["Rückfahrt im Voraus buchen", "Abholung am Hotel per WhatsApp abstimmen", "Klimatisierte Fahrzeuge"];
export default function AirportTransfer() {
  return <section id="flughafentransfer" data-nav="light" className="bg-background text-foreground py-20 md:py-24 border-b border-border">
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block */}
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-2">
            Dein Flughafentransfer
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-foreground">
            Transfer vom Flughafen Hurghada zum Hotel
          </h2>

          {/* Signature Hairline Rule with Terminal Dot */}
          <div className="relative mt-5 mb-6 flex items-center" aria-hidden="true">
            <div className="h-px bg-border w-28 md:w-36" />
            <div className="size-2 rounded-full bg-primary -ml-1 ring-2 ring-background" />
          </div>

          <p className="text-base text-muted-foreground leading-relaxed mb-10">
            Privater Flughafentransfer in Hurghada heißt: Du reist ohne fremde Mitreisende direkt zu deiner Unterkunft. Dein Fahrer bringt dich vom Flughafen zu deinem Hotel in Hurghada und Umgebung. Der Preis ist ein Festpreis pro Fahrzeug und wird vorab mit dir vereinbart.
          </p>
        </div>

        {/* Two Cards Side-by-Side */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Card 1: Ankunft */}
          <motion.div variants={itemVariants}>
            <Card className="h-full border border-border bg-card rounded-md shadow-2xs hover:border-primary/40 hover:shadow-xs transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
              <div>
                <CardHeader className="p-6 pb-4">
                  <div className="size-11 rounded-md bg-primary/10 border border-primary/25 flex items-center justify-center text-primary shrink-0 mb-4">
                    <PlaneLanding className="size-5" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-xl font-semibold tracking-tight text-foreground">
                    Ankunft: vom Flughafen zum Hotel
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <ul className="space-y-3">
                    {arrivalBullets.map(bullet => <li key={bullet} className="flex items-start gap-3 text-sm text-foreground">
                        <span className="size-5 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="size-3 stroke-[2.5]" aria-hidden="true" />
                        </span>
                        <span>{bullet}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>
            </Card>
          </motion.div>

          {/* Card 2: Rücktransfer */}
          <motion.div variants={itemVariants}>
            <Card className="h-full border border-border bg-card rounded-md shadow-2xs hover:border-primary/40 hover:shadow-xs transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
              <div>
                <CardHeader className="p-6 pb-4">
                  <div className="size-11 rounded-md bg-primary/10 border border-primary/25 flex items-center justify-center text-primary shrink-0 mb-4">
                    <PlaneTakeoff className="size-5" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-xl font-semibold tracking-tight text-foreground">
                    Rücktransfer zum Flughafen Hurghada
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <ul className="space-y-3">
                    {returnBullets.map(bullet => <li key={bullet} className="flex items-start gap-3 text-sm text-foreground">
                        <span className="size-5 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="size-3 stroke-[2.5]" aria-hidden="true" />
                        </span>
                        <span>{bullet}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Action Button */}
        <div>
          <Button asChild size="lg" className="font-semibold text-primary-foreground">
            <Link to="/BookTransferDePage" className="inline-flex items-center gap-2">
              Flughafentransfer buchen
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}